import React, { useEffect, useState } from 'react';
import { getTaxCaseById, createTask, updateTaskStatus, deleteTask, updateCaseStatus } from '../services/firestoreService';
import { useAuth } from '../context/AuthContext';
import { TaxCaseDoc, TaxStatus, TaxTask } from '../types';
import { STATUS_STEPS } from '../constants';
import CaseDocumentsPanel from '../components/CaseDocumentsPanel';
import CaseDocumentUploader from '../components/CaseDocumentUploader';
import { useTaxCaseTasks } from '../hooks/useTaxCaseTasks';

interface StaffCaseDetailProps {
  caseId: string;
  // Added optional firmId to match usage in App.tsx and fix type error
  firmId?: string;
  onBack: () => void;
}

const StaffCaseDetail: React.FC<StaffCaseDetailProps> = ({ caseId, firmId: propFirmId, onBack }) => {
  // Fix: Destruction staffFirmId from context or use propFirmId
  const { user, firmId: contextFirmId } = useAuth();
  const [caseData, setCaseData] = useState<TaxCaseDoc | null>(null);
  const [activeTab, setActiveTab] = useState<'documents' | 'tasks' | 'info'>('documents');
  
  const [newStatus, setNewStatus] = useState<TaxStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Resolve firmId from available sources
  const firmId = propFirmId || contextFirmId;

  // Tasks Logic
  const { tasks } = useTaxCaseTasks(caseId);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [newTaskType, setNewTaskType] = useState<TaxTask['type']>('GENERAL');
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  useEffect(() => {
    const fetchCase = async () => {
      if (!firmId) return;
      setLoading(true);
      try {
        const data = await getTaxCaseById(firmId, caseId);
        if (data) {
          setCaseData(data);
          setNewStatus(data.status as TaxStatus);
        } else {
          setError('Return not found.');
        }
      } catch (err) {
        console.error("Error fetching case:", err);
        setError('Failed to load return details.');
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [caseId, firmId]);

  const handleSaveStatus = async () => {
    if (!caseData || !newStatus || !firmId) return;
    setSaving(true);
    try {
      await updateCaseStatus(firmId, caseId, newStatus);
      setCaseData(prev => prev ? ({ ...prev, status: newStatus as any }) : null);
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !firmId) return;
    setIsCreatingTask(true);
    try {
      await createTask(firmId, caseId, {
        title: newTaskTitle,
        description: newTaskDesc,
        status: 'PENDING',
        type: newTaskType
      });
      setNewTaskTitle('');
      setNewTaskDesc('');
      setNewTaskType('GENERAL');
    } catch (e) {
      alert("Error creating task");
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    if (!firmId) return;
    await updateTaskStatus(firmId, caseId, taskId, currentStatus === 'PENDING' ? 'COMPLETED' : 'PENDING');
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!firmId) return;
    if (window.confirm("Are you sure you want to delete this task?")) {
      await deleteTask(firmId, caseId, taskId);
    }
  };

  if (loading) return <div className="p-12 text-center text-slate-500">Loading return details...</div>;
  if (error) return <div className="p-12 text-center text-red-600 font-bold">{error}</div>;
  if (!caseData || !user) return null;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={onBack}
          className="text-slate-500 hover:text-brand-600 transition-colors flex items-center gap-2 text-sm font-medium"
        >
          <span className="text-lg">←</span> Back to Dashboard
        </button>
        <div className="text-right">
             <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{caseData.clientName}</h2>
             <p className="text-sm text-slate-500 font-medium">{caseData.year} Tax Return • {caseData.filingType}</p>
        </div>
      </div>

      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
         <div className="flex items-center gap-4">
             <div className="w-12 h-12 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center font-black text-lg border border-brand-100">
                 {caseData.year > 9000 ? 'ID' : caseData.year.toString().slice(-2)}
             </div>
             <div>
                 <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">Current Status</p>
                 <p className="text-lg font-bold text-slate-800">{caseData.status}</p>
             </div>
         </div>

         <div className="flex items-center gap-3 w-full md:w-auto">
            <select 
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as TaxStatus)}
                className="flex-1 md:w-64 px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-white font-medium"
            >
                {STATUS_STEPS.map(step => (
                    <option key={step} value={step} className="bg-white font-medium">{step}</option>
                ))}
            </select>
            <button 
                onClick={handleSaveStatus}
                disabled={saving || newStatus === caseData.status}
                className={`px-8 py-2.5 rounded-xl text-sm font-bold text-white transition-all shadow-sm ${
                    saving || newStatus === caseData.status
                    ? 'bg-slate-300 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700 active:scale-95'
                }`}
            >
                {saving ? 'Saving...' : 'Update Status'}
            </button>
         </div>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit mb-6">
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-6 py-2 text-sm font-bold rounded-lg transition-all ${
            activeTab === 'documents' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Documents
        </button>
        <button
          onClick={() => setActiveTab('tasks')}
          className={`px-6 py-2 text-sm font-bold rounded-lg transition-all ${
            activeTab === 'tasks' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Client Tasks
        </button>
        <button
          onClick={() => setActiveTab('info')}
          className={`px-6 py-2 text-sm font-bold rounded-lg transition-all ${
            activeTab === 'info' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Internal Info
        </button>
      </div>

      <div className="animate-fade-in">
        {activeTab === 'documents' && (
           <div className="flex flex-col gap-8">
             <CaseDocumentsPanel caseId={caseId} isStaff={true} />
             
             <div className="w-full">
               <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-xs text-amber-800 flex items-start gap-3">
                  <div className="mt-0.5 text-lg">💡</div>
                  <div>
                    <p className="font-bold mb-1">Upload Note</p>
                    <p>Files uploaded here are visible to the client immediately. Please ensure sensitive internal working papers are kept in the Internal Info tab.</p>
                  </div>
               </div>
               <CaseDocumentUploader caseId={caseId} currentUserUid={user.uid} />
             </div>
           </div>
        )}

        {activeTab === 'tasks' && (
           <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                 <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                       <h3 className="font-bold text-slate-800">Current Tasks for Client</h3>
                       <span className="text-xs font-bold text-brand-600 bg-brand-50 px-2 py-1 rounded-full">
                          {tasks.filter(t => t.status === 'COMPLETED').length} / {tasks.length} Completed
                       </span>
                    </div>
                    
                    <div className="divide-y divide-slate-100">
                       {tasks.length === 0 ? (
                         <div className="p-12 text-center text-slate-400">
                            No tasks assigned yet.
                         </div>
                       ) : (
                         tasks.map(task => (
                           <div key={task.id} className="p-4 flex items-start gap-4 hover:bg-slate-50/50 transition-colors group">
                              <button 
                                 onClick={() => handleToggleTask(task.id, task.status)}
                                 className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                                    task.status === 'COMPLETED' ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-300 bg-white'
                                 }`}
                              >
                                 {task.status === 'COMPLETED' && <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                              </button>
                              <div className="flex-1">
                                 <div className="flex items-center gap-2">
                                    <h4 className={`font-semibold ${task.status === 'COMPLETED' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                                       {task.title}
                                    </h4>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase font-bold tracking-tight">
                                       {task.type}
                                    </span>
                                 </div>
                                 <p className={`text-sm mt-1 ${task.status === 'COMPLETED' ? 'text-slate-300' : 'text-slate-500'}`}>
                                    {task.description}
                                 </p>
                              </div>
                              <button 
                                 onClick={() => handleDeleteTask(task.id)}
                                 className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all p-1"
                              >
                                 <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                           </div>
                         ))
                       )}
                    </div>
                 </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm h-fit sticky top-4">
                 <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <span>➕</span> Create New Task
                 </h3>
                 <form onSubmit={handleAddTask} className="space-y-4">
                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Task Title</label>
                       <input 
                         type="text"
                         required
                         placeholder="e.g., Upload Spouse W-2"
                         value={newTaskTitle}
                         onChange={(e) => setNewTaskTitle(e.target.value)}
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                    </div>
                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Description</label>
                       <textarea 
                         rows={3}
                         placeholder="Instructions for the client..."
                         value={newTaskDesc}
                         onChange={(e) => setNewTaskDesc(e.target.value)}
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                       />
                    </div>
                    <div>
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Task Type</label>
                       <select 
                         value={newTaskType}
                         onChange={(e) => setNewTaskType(e.target.value as any)}
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                       >
                          <option value="GENERAL">General Checklist</option>
                          <option value="UPLOAD">Document Upload</option>
                          <option value="SIGN">Digital Signature</option>
                       </select>
                    </div>
                    <button 
                       type="submit"
                       disabled={isCreatingTask}
                       className="w-full bg-brand-600 text-white py-2.5 rounded-lg font-bold hover:bg-brand-700 shadow-md transition-all flex items-center justify-center gap-2"
                    >
                       {isCreatingTask ? 'Adding...' : 'Assign Task'}
                    </button>
                 </form>
              </div>
           </div>
        )}

        {activeTab === 'info' && (
           <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm w-full">
              <h3 className="font-bold text-slate-800 mb-6">Internal Engagement Details</h3>
              <dl className="space-y-0 text-sm">
                 <div className="grid grid-cols-3 border-b border-slate-100 py-4 items-center">
                    <dt className="text-slate-500 font-medium">Firestore ID</dt>
                    <dd className="col-span-2">
                       <span className="font-mono text-slate-700 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                         {caseId}
                       </span>
                    </dd>
                 </div>
                 <div className="grid grid-cols-3 border-b border-slate-100 py-4 items-center">
                    <dt className="text-slate-500 font-medium">Client UID</dt>
                    <dd className="col-span-2 font-mono text-slate-700">{caseData.clientId}</dd>
                 </div>
                 <div className="grid grid-cols-3 border-b border-slate-100 py-4 items-center">
                    <dt className="text-slate-500 font-medium">Assigned Staff</dt>
                    <dd className="col-span-2 font-bold text-brand-600">{caseData.assignedStaffName || 'Unassigned'}</dd>
                 </div>
                 <div className="grid grid-cols-3 py-4 items-center">
                    <dt className="text-slate-500 font-medium">Last Updated</dt>
                    <dd className="col-span-2 text-slate-700 font-medium">
                        {caseData.lastUpdated?.toDate?.().toLocaleString() || new Date().toLocaleString()}
                    </dd>
                 </div>
              </dl>
           </div>
        )}
      </div>
    </div>
  );
};

export default StaffCaseDetail;